import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { Link, navigate } from 'gatsby';
import { getImage, GatsbyImage, IGatsbyImageData } from 'gatsby-plugin-image';

import { useAppDispatch } from '../types/hooks';
import { Post } from '../types/types';
import { changePlayerInfo, changePodcastInfo } from '../slices/AppSlice';
import GuardarIcon from '../images/guardar.svg';
import PlayIcon from '../images/play-negro.svg';
import LeerIcon from '../images/leer.svg';

const StyledPostItemContainer = styled.div<{ $isLastItem: boolean }>`
  margin-bottom: ${(props: any) => (props.$isLastItem ? '0' : '0.75rem')};
  border-bottom: ${(props: any) => (props.$isLastItem ? 'none' : '1px solid var(--border-gray)')};
  @media (min-width: 576px) {
    padding-bottom: ${(props: any) => (props.$isLastItem ? '0' : '0.75rem')};
  }
  .post-item-container {
    display: flex;
    flex-direction: column;
    padding-bottom: ${(props: any) => (props.$isLastItem ? '1.5rem' : '1rem')};
    .post-header {
      display: grid;
      align-items: center;
      grid-template-columns: ${(props: any) => (!props.$noImage ? '9rem 1fr' : '1fr')};

      .gatsby-image-wrapper {
        cursor: pointer;
        border-radius: ${(props: any) => props.$borderRadius};
        height: 100%;
        width: 9rem;
        height: 7.1rem;
        img {
          border-radius: ${(props: any) => props.$borderRadius};
        }
      }
    }
    @media (min-width: 576px) {
      padding-bottom: 0;
      &.vertical-on-desktop {
        .post-header {
          display: flex;
          flex-direction: column;
          row-gap: 1rem;
          .gatsby-image-wrapper {
            border-radius: 15px;
          }
          .post-title-container {
            a {
              color: var(--verde-principal);
              margin: 0;
            }
            a:last-child {
              margin-left: 0;
              font-size: 1.2rem;
            }
          }
        }
      }
    }
    .post-title-container {
      cursor: pointer;
      a {
        font-family: 'TradeGothicN2';
        margin: ${(props: any) => (!props.noImage ? '0 0 0.25rem 1rem' : '0')};
        color: var(--rojo-principal);
        text-transform: uppercase;
        font-size: 0.8rem;
        display: block;
        font-weight: 600;
        @media (min-width: 576px) {
          font-size: 0.85rem;
        }
      }
      a:last-child {
        margin-left: ${(props: any) => (!props.noImage ? '1rem' : '0')};
        font-family: 'TradeGothicN2';
        font-size: 0.9rem;
        color: black;
        text-transform: none;
        font-weight: 400;
        @media (min-width: 576px) {
          font-size: 1.05rem;
        }
      }
      .more-link {
        display: none;
      }
    }
    .buttons-container {
      display: flex;
      justify-content: space-between;
      button {
        font-family: 'TradeGothicN2';
        background-color: transparent;
        border: none;
        display: flex;
        align-items: center;
        margin-top: 0.75rem;
        font-size: 1rem;
        @media (min-width: 576px) {
          display: none;
        }
        img {
          width: 1.4rem;
          height: 1.4rem;
        }
        &.save-post-btn {
          img {
            margin-right: 0.5rem;
          }
        }
        &.listen-btn {
          justify-content: flex-end;
          img {
            margin-left: 0.5rem;
          }
        }
      }
      a {
        justify-content: flex-end;
        color: black;
        text-decoration: none;
        font-family: 'TradeGothicN2';
        font-size: 1rem;
        display: flex;
        align-items: center;
        margin-top: 0.75rem;
        img {
          margin-left: 0.5rem;
          width: 1.9rem;
          height: 1.9rem;
        }
        @media (min-width: 576px) {
          display: none;
        }
      }
    }
  }
`;

interface PostItemProps {
  post: Post;
  isLastItem: boolean;
  onlyRead?: boolean;
  verticalOnDesktop?: boolean;
  noImage?: boolean;
  borderRadius?: string;
  noSaveButton?: boolean;
  noReadButton?: boolean;
}

const PostItem = ({
  post,
  isLastItem,
  onlyRead = false,
  verticalOnDesktop = false,
  noImage = false,
  borderRadius = '0',
  noSaveButton = true,
  noReadButton = false,
}: PostItemProps) => {
  const dispatch = useAppDispatch();
  const [featureImage, setFeatureImage] = useState<IGatsbyImageData>();

  useEffect(() => {
    if (post.featuredImage) {
      setFeatureImage(getImage(post.featuredImage.node.localFile));
    }
  }, []);

  const goToPostPage = (playAudio: boolean) => {
    if (playAudio && post.afc_post) {
      dispatch(
        changePlayerInfo({
          playerSlug: post.categories.nodes[0].slug,
          playerUrl: post.afc_post.audioDeLaNota.mediaItemUrl,
          timeStamp: new Date().getUTCMilliseconds(),
        }),
      );
      dispatch(changePodcastInfo({ podcastTitle: post.title, podcastSlug: post.slug }));
    }
    navigate(`/${post.slug}`);
  };

  return (
    <StyledPostItemContainer
      $borderRadius={borderRadius}
      $isLastItem={isLastItem}
      $noImage={noImage}
    >
      <div className={`post-item-container ${verticalOnDesktop ? 'vertical-on-desktop' : ''}`}>
        <div className="post-header">
          {featureImage && !noImage && (
            <Link to={`/${post.slug}`}>
              <GatsbyImage
                alt="Imagen del artículo"
                image={featureImage}
                onClick={() => goToPostPage(false)}
              />
            </Link>
          )}
          <div className="post-title-container">
            {post.tags.nodes.length > 0 && (
              <Link to={`/${post.slug}`}>{post.tags.nodes[0].name}</Link>
            )}
            <Link to={`/${post.slug}`}>{post.title}</Link>
          </div>
        </div>
        <div className="buttons-container">
          {noSaveButton ? (
            <div />
          ) : (
            <button className="save-post-btn">
              <img alt="Icono de guardar noticia" src={GuardarIcon} />
              Guardar
            </button>
          )}

          {post.afc_post && !onlyRead && (
            <button className="listen-btn" onClick={() => goToPostPage(true)}>
              Escuchar
              <img alt="Icono de escuchar la nota" src={PlayIcon} />
            </button>
          )}
          {((!post.afc_post && !noReadButton) || (onlyRead && !noReadButton)) && (
            <Link className="read-btn" to={post.slug}>
              Leer
              <img alt="Icono de leer la nota" src={LeerIcon} />
            </Link>
          )}
        </div>
      </div>
    </StyledPostItemContainer>
  );
};

export default PostItem;
